import request from '@/api-v2/request';
import type { IDataDiscoveryTrackResponse } from './types/IDataDiscoveryTrackResponse';
import type { IDataDiscoveryMstRequest } from './types/IDataDiscoveryMstRequest';
import type { DataDiscoveryResponseDTO } from './types/DataDiscoveryResponseDTO';
import type {
  DataDiscoveryMetricsRequestDTO,
  DataDiscoveryUniverseMetricsResponseDTO,
  DataDiscoveryPortfolioMetricsResponseDTO,
  DataDiscoveryVolScaledPortfolioMetricsRequestDTO,
  DataDiscoveryVolScaledStrategyMetricsRequestDTO,
} from './types/DataDiscoveryMetricsDTO';
import type { PortfolioSummaryResponseDTO } from './types/PortfolioSummaryResponseDTO';
import type { IMetric } from '@/types/strategy';
import type { IMstResponse } from '@/types/IMstResponse';
import type { DataDiscoveryTrackRequestDTO } from './types/DataDiscoveryTrackRequestDTO';
import type {
  DataDiscoveryUniverseQuantSpaceRequestDTO,
  DataDiscoveryUniverseQuantSpaceResponseDTO,
} from './types/DataDiscoveryQuantSpaceDTO';
import type { IFxRatesQuery } from '@/types/IFxRatesQuery';
import type { StrategyItemResponseDTO } from './types/StrategyItemResponseDTO';
import type { PortfolioItemResponseDTO } from './types/PortfolioItemResponseDTO';
import type { StrategyCategoryResponseDTO } from './types/StrategyCategoryResponseDTO';

export const getUniverse = ({ signal }: { signal: AbortSignal }): Promise<DataDiscoveryResponseDTO> =>
  request({
    method: 'get',
    url: `/discover`,
    signal,
  }).then((o): DataDiscoveryResponseDTO => o.data);

export const getPortfolios = ({ signal }: { signal: AbortSignal }): Promise<PortfolioItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/portfolios`,
    signal,
  }).then((o): PortfolioItemResponseDTO[] => o.data);

export const getNonVolScaledUniverseMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getNonVolScaledPortfoliosMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryPortfolioMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/portfolios/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryPortfolioMetricsResponseDTO => o.data);

export const getEquities = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/equities`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getEquitiesMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/equities/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getStructuredProducts = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/structured-products`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getStructuredProductMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/structured-products/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getEvestmentFunds = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/evestment`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getEvestmentFundsMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/evestment/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getMorningstarFunds = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/morningstar`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getMorningstarFundsMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/morningstar/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getPrivateTracks = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/private-tracks`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getRetiredStrategies = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/retired-strategies`,
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);

export const getStrategyCategories = ({ signal }: { signal: AbortSignal }): Promise<StrategyCategoryResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/categories`,
    signal,
  }).then((o): StrategyCategoryResponseDTO[] => o.data);

export const getPrivateTracksMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseMetricsResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/private-tracks/metrics`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseMetricsResponseDTO => o.data);

export const getQuantSpacePositions = ({
  params,
  signal,
}: {
  params: DataDiscoveryUniverseQuantSpaceRequestDTO;
  signal: AbortSignal;
}): Promise<DataDiscoveryUniverseQuantSpaceResponseDTO> =>
  request({
    method: 'get',
    url: `/discover/quant-space`,
    params,
    signal,
  }).then((o): DataDiscoveryUniverseQuantSpaceResponseDTO => o.data);

/**
 * Fetches metrics for a strategy for a given period
 * @param code Strategy code to fetch metrics for
 * @param volScaling Should volatility scaling be applied
 * @param volScalePeriod Period for which track needs to be Volatility scaled
 * @param analyticsPeriod Analytics period for which metrics needs to be calculated
 */
export const getVolScaledUniverseMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryVolScaledStrategyMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<{ result: IMetric }> => {
  return request({
    method: 'get',
    url: `/discover/strategy/${params.code}/metrics`,
    params,
    signal,
  }).then((o) => o.data);
};

/**
 * Fetches metrics for a portfolio for a given period
 * @param slug Portfolio slug to fetch metrics for
 * @param volScaling Should volatility scaling be applied
 * @param volScalePeriod Period for which track needs to be Volatility scaled
 * @param analyticsPeriod Analytics period for which metrics needs to be calculated
 */
export const getVolScaledPortfolioMetrics = ({
  params,
  signal,
}: {
  params: DataDiscoveryVolScaledPortfolioMetricsRequestDTO;
  signal: AbortSignal;
}): Promise<{ result: IMetric }> => {
  return request({
    method: 'get',
    url: `/discover/portfolio/${params.slug}/metrics`,
    params,
    signal,
  }).then((o) => o.data);
};

export const getStrategyTrackWithParams = ({
  code,
  params,
  signal,
}: {
  code: string;
  params: DataDiscoveryTrackRequestDTO;
  signal: AbortSignal;
}): Promise<IDataDiscoveryTrackResponse> =>
  request({
    method: 'get',
    url: `/discover/strategy/${code}/track`,
    params,
    signal,
  }).then((o) => o.data);

export const getPortfolioTrackWithParams = ({
  slug,
  params,
  signal,
}: {
  slug: string;
  params: DataDiscoveryTrackRequestDTO;
  signal: AbortSignal;
}): Promise<IDataDiscoveryTrackResponse> =>
  request({
    method: 'get',
    url: `/discover/portfolio/${slug}/track`,
    params,
    signal,
  }).then((o) => o.data);

export const getMst = ({
  data,
  signal,
}: {
  data: IDataDiscoveryMstRequest;
  signal: AbortSignal;
}): Promise<IMstResponse> =>
  request({
    method: 'post',
    url: `/discover/mst`,
    data,
    signal,
  }).then((o) => o.data);

export const getPortfolioSummary = ({ signal }: { signal: AbortSignal }): Promise<PortfolioSummaryResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/portfolios/summary`,
    signal,
  }).then((o) => o.data);

export const getFxRates = (query: IFxRatesQuery, signal: AbortSignal): Promise<{ [key: string]: number }> =>
  request({
    url: `/discover/fx-rates`,
    method: 'get',
    params: query,
    signal,
  }).then((o): { [key: string]: number } => o.data);

export const getCash = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    method: 'get',
    url: `/discover/cash`,
    signal,
  }).then((o) => o.data);

export const getSignals = ({ signal }: { signal: AbortSignal }): Promise<StrategyItemResponseDTO[]> =>
  request({
    url: `/discover/signals`,
    method: 'get',
    signal,
  }).then((o): StrategyItemResponseDTO[] => o.data);
